import { LitElement, html, nothing, type CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { emit } from '../utils/dom.utils.js';
import { FormAssociatedMixin }      from '../utils/form-associated.mixin.js';
import { FormValidationController } from '../utils/form-validation.controller.js';
import type { Size } from '../types.js';
import styles from './styles.js';

export type SelectButtonSize    = Size;
export type SelectButtonRawOpt  = string | number | Record<string, unknown>;

/**
 * uwc-selectbutton
 *
 * A segmented button group that acts as a single-select (radio) or multi-select
 * (checkbox) control. Options can be plain strings / numbers or objects with
 * configurable `label`, `value`, `disabled`, and `icon` fields.
 *
 * @attr {boolean}        multiple        - Allow selecting multiple options simultaneously.
 * @attr {boolean}        allow-empty     - Allow deselecting the last option in single mode. Default: true.
 * @attr {boolean}        disabled        - Disable all interaction.
 * @attr {boolean}        invalid         - Apply error / invalid styling.
 * @attr {boolean}        fluid           - Stretch the component to fill its container width.
 * @attr {'small'|'medium'|'large'} size  - Button size preset. Default: medium.
 * @attr {string}         option-label    - Object key used as display label. Default: label.
 * @attr {string}         option-value    - Object key used as the emitted value. Default: value.
 * @attr {string}         option-disabled - Object key for the per-option disabled flag. Default: disabled.
 * @attr {string}         label           - Accessible label for the button group (aria-label).
 *
 * @prop {SelectButtonRawOpt[]}   options - Array of option items to render.
 * @prop {unknown | unknown[]}    value   - Current selection. Array when multiple=true.
 *
 * @fires uwc-change - Fired on every selection change. Detail: `{ value }` — single value or array.
 *
 * @csspart button - Each individual option button element.
 *
 * @cssprop --uwc-sb-bg               - Unselected button background. Default: surface token.
 * @cssprop --uwc-sb-border           - Border color between buttons and around the group. Default: border-control token.
 * @cssprop --uwc-sb-radius           - Corner radius of the button group. Default: radius-md token.
 * @cssprop --uwc-sb-color            - Unselected button text color. Default: text token.
 * @cssprop --uwc-sb-hover-bg         - Background of an unselected button on hover. Default: hover-bg token.
 * @cssprop --uwc-sb-selected-bg      - Background of a selected button. Default: primary token.
 * @cssprop --uwc-sb-selected-color   - Text color of a selected button. Default: white.
 * @cssprop --uwc-sb-selected-border  - Divider color adjacent to a selected button. Default: primary token.
 * @cssprop --uwc-sb-font-size        - Button label font size.
 * @cssprop --uwc-sb-padding-x        - Horizontal padding inside each button.
 * @cssprop --uwc-sb-padding-y        - Vertical padding inside each button.
 */
export class UwcSelectButton extends FormAssociatedMixin(LitElement) {
  static override styles: CSSResultGroup = [styles];

  private _validation = new FormValidationController(this);

  // ── Data ───────────────────────────────────────────────────────────────────

  /** Array of option items to render. */
  @property({ attribute: false }) options: SelectButtonRawOpt[] = [];

  /** Current selection — a single value or an array when multiple=true. */
  @property({ attribute: false }) value: unknown | unknown[] = null;

  // ── Behaviour ──────────────────────────────────────────────────────────────

  /** Allow multiple options to be selected simultaneously. */
  @property({ type: Boolean, reflect: true }) multiple = false;

  /** Allow deselecting the last remaining option in single mode. */
  @property({ type: Boolean, attribute: 'allow-empty' }) allowEmpty = true;

  /** Require a selection. */
  @property({ type: Boolean, reflect: true }) required = false;

  /** Disable all pointer and keyboard interaction. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /** Mark the component as invalid, applying error styling. */
  @property({ type: Boolean, reflect: true }) invalid = false;

  /** Stretch the component to fill its container width. */
  @property({ type: Boolean, reflect: true }) fluid = false;

  /** Button size variant — `'small'`, `'medium'` (default), or `'large'`. */
  @property({ reflect: true }) size: SelectButtonSize = 'medium';

  // ── Option field mapping ───────────────────────────────────────────────────

  /** Object property used as the display label. Default: `label`. */
  @property({ attribute: 'option-label' }) optionLabel = 'label';

  /** Object property used as the selection value. Default: `value`. */
  @property({ attribute: 'option-value' }) optionValue = 'value';

  /** Object property that marks an option as disabled. Default: `disabled`. */
  @property({ attribute: 'option-disabled' }) optionDisabled = 'disabled';

  /** Accessible label for the button group (maps to `aria-label`). */
  @property() label = '';

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  private _hasValue(): boolean {
    if (this.value == null) return false;
    if (Array.isArray(this.value)) return (this.value as unknown[]).length > 0;
    return true;
  }

  override firstUpdated() {
    this._validation.syncFormValue(this._hasValue() ? JSON.stringify(this.value) : null);
    this._validation.revalidate(this._hasValue() ? 'v' : '', { required: this.required });
  }

  override updated(changed: Map<PropertyKey, unknown>) {
    super.updated(changed);
    if (changed.has('required')) {
      this._validation.revalidate(this._hasValue() ? 'v' : '', { required: this.required });
    }
  }

  // ── Option helpers ─────────────────────────────────────────────────────────

  private _getLabel(opt: SelectButtonRawOpt): string {
    if (typeof opt === 'object' && opt !== null) {
      return String(opt[this.optionLabel] ?? '');
    }
    return String(opt);
  }

  private _getValue(opt: SelectButtonRawOpt): unknown {
    if (typeof opt === 'object' && opt !== null) {
      return this.optionValue in opt ? opt[this.optionValue] : opt;
    }
    return opt;
  }

  private _isOptDisabled(opt: SelectButtonRawOpt): boolean {
    if (typeof opt === 'object' && opt !== null) {
      return Boolean(opt[this.optionDisabled]);
    }
    return false;
  }

  private _getIcon(opt: SelectButtonRawOpt): string | undefined {
    if (typeof opt === 'object' && opt !== null) {
      const icon = opt['icon'];
      return typeof icon === 'string' ? icon : undefined;
    }
    return undefined;
  }

  private _isSelected(optValue: unknown): boolean {
    if (this.multiple) {
      return Array.isArray(this.value) && (this.value as unknown[]).includes(optValue);
    }
    return this.value === optValue;
  }

  // ── Interaction ────────────────────────────────────────────────────────────

  private _select(optValue: unknown, optDisabled: boolean, e: Event) {
    if (this.disabled || optDisabled) return;

    if (this.multiple) {
      const current = Array.isArray(this.value) ? [...(this.value as unknown[])] : [];
      const idx = current.indexOf(optValue);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        current.push(optValue);
      }
      this.value = current;
    } else {
      if (this.value === optValue) {
        if (!this.allowEmpty) return;
        this.value = null;
      } else {
        this.value = optValue;
      }
    }

    const hasVal = this._hasValue();
    this._validation.handleChange(hasVal ? 'v' : '', { required: this.required });
    this._validation.setTouched(true);
    this._validation.syncFormValue(hasVal ? JSON.stringify(this.value) : null);
    emit(this, 'uwc-change', { value: this.value, originalEvent: e });
  }

  private _onKeydown(optValue: unknown, optDisabled: boolean, idx: number, e: KeyboardEvent) {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      this._select(optValue, optDisabled, e);
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      this._focusBtn(idx + 1);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      this._focusBtn(idx - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      this._focusBtn(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      this._focusBtn(this.options.length - 1);
    }
  }

  private _focusBtn(idx: number) {
    const btns = this.shadowRoot?.querySelectorAll<HTMLElement>('[part="button"]');
    if (!btns) return;
    const clamped = Math.max(0, Math.min(idx, btns.length - 1));
    btns[clamped]?.focus();
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  override render() {
    const groupRole = this.multiple ? 'group' : 'radiogroup';

    return html`
      <div
        class=${classMap({
          'uwc-sb':          true,
          'uwc-sb--invalid': this.invalid,
        })}
        role=${groupRole}
        aria-label=${this.label || 'Select button'}
        aria-disabled=${this.disabled ? 'true' : 'false'}
      >
        ${this.options.map((opt, idx) => {
          const optValue    = this._getValue(opt);
          const optLabel    = this._getLabel(opt);
          const optDis      = this._isOptDisabled(opt) || this.disabled;
          const icon        = this._getIcon(opt);
          const selected    = this._isSelected(optValue);
          const btnRole     = this.multiple ? 'checkbox' : 'radio';

          return html`
            <button
              part="button"
              class=${classMap({
                'uwc-sb__btn':           true,
                'uwc-sb__btn--selected': selected,
                'uwc-sb__btn--disabled': optDis,
              })}
              role=${btnRole}
              aria-checked=${selected ? 'true' : 'false'}
              aria-disabled=${optDis ? 'true' : 'false'}
              tabindex=${optDis ? -1 : 0}
              ?disabled=${optDis}
              @click=${(e: Event) => this._select(optValue, optDis, e)}
              @keydown=${(e: KeyboardEvent) => this._onKeydown(optValue, optDis, idx, e)}
            >
              ${icon ? html`<uwc-icon name=${icon} class="uwc-sb__icon"></uwc-icon>` : nothing}
              <span class="uwc-sb__label">${optLabel}</span>
            </button>
          `;
        })}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-selectbutton': UwcSelectButton; }
}
